import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import { CLIENT_EMIT_CLAIM_SESSION, CONNECT, RECONNECT_FAILED, SERVER_EMIT_SESSION_REPLACED } from 'services/socket-connection/events';
import {
    CLAIM_ACK_TIMEOUT_MS,
    claimModeOnConnect,
    emitsToReplayAfterGrant,
    isServerGrant,
    markResumeOnNextLoad,
    nextStateFromClaim,
    shouldForceAfterBusy,
    takeResumeSessionId,
    TClaimMode,
    TClaimResult,
    TSessionState
} from './sessionClaim';

export interface ISessionClaim {
    sessionState: TSessionState;
    sessionStateRef: MutableRefObject<TSessionState>;
    /** "Use here" on the waiting screen: takes the session for this tab; asks first only if the other one is in a call. */
    claimHere: () => void;
    /** "Use here" / "Continue here" on the prompt: the user chose to take over from the other tab or device. */
    continueHere: () => void;
    /** "Keep the other one" / "Cancel" on the prompt: leave the other session alone and try to close this tab. */
    keepOtherSession: () => void;
    /** Increments on every grant, including fail-open; send per-grant state from an effect keyed on it so child handlers are registered first. */
    grantCount: number;
    /** Sends the emits queued while disconnected (see emitsToReplayAfterGrant); call after the per-grant state. */
    replayHeldEmits: () => void;
}

// One id per page load, shared by every SocketProvider mount: in-app navigation remounts Core, and a new id would make
// the server treat the tab's own previous socket as another session. A reload after `online` keeps the previous id (see markResumeOnNextLoad).
// Module-level (not component state) so it also survives StrictMode's double mount-render of a mounting component.
let pageSession: { id: string; isResumeAfterReload: boolean } | undefined;
// Read and cleared as the page loads (this module loads with the app), not when Campus first mounts: a marker left by an exit
// that wasn't a reload (logout to /login, leaving the site) must not stay in sessionStorage, where "Duplicate tab" would copy it.
const resumeIdFromPreviousPage = takeResumeSessionId();
const pageSessionForThisLoad = (): { id: string; isResumeAfterReload: boolean } => {
    if (!pageSession) {
        pageSession = { id: resumeIdFromPreviousPage ?? uuidv4(), isResumeAfterReload: resumeIdFromPreviousPage !== null };
    }
    return pageSession;
};

// Page-level, so an in-app navigation (Core remount) continues where the previous mount left off.
let pageHasHeldSession = false; // replaces the per-mount hasBeenActiveRef
let pageLastState: TSessionState | undefined; // last state any mount of this page reached

/** A remount of a page that is using the session keeps showing Campus while it resumes; a replaced page stays on the takeover screen. */
const initialStateForMount = (): TSessionState => (pageLastState === 'active' ? 'active' : pageLastState === 'replaced' ? 'replaced' : 'claiming');

/**
 * Claims the user's session from the realtime server on every (re)connect. The server keeps one live session per user,
 * and a new tab or device never takes over on its own: when a session is already open the user is asked
 * ("Use here" or "Keep the other one"), with a stronger warning when that session is in a call.
 * Only the first claim and claims from the takeover screen show `claiming`; a reconnect (or a Core remount) stays `active` while it resumes.
 */
export const useSessionClaim = (socket: Socket): ISessionClaim => {
    // In memory on purpose: "Duplicate tab" copies sessionStorage, which would give two tabs the same id.
    // One id per page load (see pageSessionForThisLoad), shared across Core remounts and, after an `online` reload, with the previous load.
    const sessionIdRef = useRef(pageSessionForThisLoad().id);
    // Module-level state is read at the first render, so StrictMode's double mount-render starts both from the same state.
    const [sessionState, setSessionState] = useState<TSessionState>(initialStateForMount);
    const sessionStateRef = useRef<TSessionState>(sessionState);
    const [grantCount, setGrantCount] = useState(0);
    // A claim the takeover screen asked for while disconnected; sent on the next connect.
    const requestedClaimRef = useRef<{ mode: TClaimMode; isExplicitClaim: boolean } | null>(null);
    const claimInFlightRef = useRef(false);
    // Bumped on every claim and on replacement, so a late answer to an older claim is ignored.
    const claimSeqRef = useRef(0);
    // Emits queued while disconnected, sent again after the next grant (see handleTransportOpen).
    const heldEmitsRef = useRef<unknown[][]>([]);

    const applyState = useCallback(
        (next: TSessionState, isRealGrant = false) => {
            sessionStateRef.current = next;
            pageLastState = next;
            setSessionState(next);
            if (next === 'active') {
                // Fail-open doesn't count as ever having held the session: only a real server grant does, so a later
                // reconnect still claims (not resumes) once the server is actually reachable.
                if (isRealGrant) pageHasHeldSession = true;
                setGrantCount((count) => count + 1);
            }
            if (next === 'replaced') {
                // This tab no longer holds the session: the server would drop them.
                heldEmitsRef.current = [];
                socket.disconnect();
            }
        },
        [socket]
    );

    /** Ignores any claim still in flight and shows the takeover screen. */
    const becomeReplaced = useCallback(() => {
        claimSeqRef.current += 1;
        claimInFlightRef.current = false;
        applyState('replaced');
    }, [applyState]);

    const replayHeldEmits = useCallback(() => {
        const held = heldEmitsRef.current;
        heldEmitsRef.current = [];
        held.forEach((data) => {
            const [event, ...args] = data as [string, ...unknown[]];
            socket.emit(event, ...args);
        });
    }, [socket]);

    const sendClaim = useCallback(
        (mode: TClaimMode, isExplicitClaim = false) => {
            const emitClaim = (claimMode: TClaimMode) => {
                const seq = ++claimSeqRef.current;
                claimInFlightRef.current = true;
                socket
                    .timeout(CLAIM_ACK_TIMEOUT_MS)
                    .emit(CLIENT_EMIT_CLAIM_SESSION, { sessionId: sessionIdRef.current, mode: claimMode }, (error: Error | null, result?: TClaimResult) => {
                        if (seq !== claimSeqRef.current) return;
                        claimInFlightRef.current = false;
                        const next = nextStateFromClaim(error ? undefined : result);
                        // "Use here" was already the user's choice: don't ask again unless the other session is in a call.
                        if (shouldForceAfterBusy(next, isExplicitClaim, claimMode)) return emitClaim('force');
                        applyState(next, isServerGrant(error ? undefined : result));
                    });
            };
            emitClaim(mode);
        },
        [socket, applyState]
    );

    useEffect(() => {
        const handleTransportOpen = () => {
            // The claim must be the first thing the server sees on every (re)connect: socket.io-client flushes any
            // buffered emits (queued while disconnected) before the "connect" event fires, which would otherwise
            // let them reach the server ahead of our claim, where they'd be dropped. Hold them until the grant instead;
            // the grant re-sends profile, status and call state fresh.
            heldEmitsRef.current.push(...emitsToReplayAfterGrant(socket.sendBuffer));
            socket.sendBuffer.length = 0;
        };
        const handleConnect = () => {
            const requested = requestedClaimRef.current;
            requestedClaimRef.current = null;
            const mode = claimModeOnConnect(requested?.mode ?? null, pageHasHeldSession || pageSessionForThisLoad().isResumeAfterReload);
            sendClaim(mode, requested?.isExplicitClaim ?? false);
        };
        const handleReplaced = becomeReplaced;
        const handleDisconnect = (reason: Socket.DisconnectReason) => {
            // A tab asking the user never held the session and its question may be out of date after a reconnect.
            // Show the waiting screen instead; "Use here" claims again. Our own disconnect (unmount closes the socket)
            // doesn't reconnect, so it doesn't count.
            const isAsking = sessionStateRef.current === 'busy-in-call' || sessionStateRef.current === 'busy-open-elsewhere';
            if (!isAsking || reason === 'io client disconnect') return;
            becomeReplaced();
        };

        // A refresh of a tab that holds the session keeps its session id, so the reloaded page isn't asked about itself.
        // A page kept in the back/forward cache isn't reloaded: its marker would only sit there for "Duplicate tab" to copy.
        const handlePageHide = (event: PageTransitionEvent) => {
            if (!event.persisted && sessionStateRef.current === 'active') markResumeOnNextLoad(sessionIdRef.current);
        };

        window.addEventListener('pagehide', handlePageHide);
        socket.io.on('open', handleTransportOpen);
        socket.on(CONNECT, handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on(SERVER_EMIT_SESSION_REPLACED, handleReplaced);
        if (socket.connected) handleConnect();

        return () => {
            window.removeEventListener('pagehide', handlePageHide);
            socket.io.off('open', handleTransportOpen);
            socket.off(CONNECT, handleConnect);
            socket.off('disconnect', handleDisconnect);
            socket.off(SERVER_EMIT_SESSION_REPLACED, handleReplaced);
        };
    }, [socket, sendClaim, becomeReplaced]);

    // Fail-open when the server can't be reached at all: the ack timeout only starts once a claim is sent, which needs a connection.
    // Also as soon as socket.io stops reconnecting, since no claim can be sent after that.
    // Only while `claiming`: a mount that starts `active` (Core remount) resumes in the background.
    useEffect(() => {
        if (sessionState !== 'claiming') return;
        const failOpen = () => {
            if (sessionStateRef.current === 'claiming' && !claimInFlightRef.current) {
                // Clears a stale "force" requested from the takeover screen before we failed open, so it can't fire later.
                requestedClaimRef.current = null;
                applyState('active', false);
            }
        };
        const timer = setTimeout(failOpen, CLAIM_ACK_TIMEOUT_MS);
        socket.io.on(RECONNECT_FAILED, failOpen);
        return () => {
            clearTimeout(timer);
            socket.io.off(RECONNECT_FAILED, failOpen);
        };
    }, [sessionState, applyState, socket]);

    const claimWith = useCallback(
        (mode: TClaimMode, isExplicitClaim: boolean) => {
            sessionStateRef.current = 'claiming';
            setSessionState('claiming');
            if (socket.connected) {
                sendClaim(mode, isExplicitClaim);
            } else {
                requestedClaimRef.current = { mode, isExplicitClaim };
                socket.connect();
            }
        },
        [socket, sendClaim]
    );

    const claimHere = useCallback(() => claimWith('initial', true), [claimWith]);
    const continueHere = useCallback(() => claimWith('force', false), [claimWith]);
    const keepOtherSession = useCallback(() => {
        becomeReplaced();
        // Browsers only let a script close a tab that a script opened; for a tab the user opened this does nothing
        // and the waiting screen ("Use here") stays.
        window.close();
    }, [becomeReplaced]);

    return { sessionState, sessionStateRef, claimHere, continueHere, keepOtherSession, grantCount, replayHeldEmits };
};
